import { useCallback, useEffect, useRef, useState } from 'react';

import HourlyScrollItem from '@/components/hourly-scroll/HourlyScrollItem';
import ScrollButton from '@/components/hourly-scroll/ScrollButton';
import { HourlyForecast } from '@/models/HourlyForecast';

type HourlyScrollBarProps = {
  hourlyForecast: HourlyForecast[];
};

// width of item on XL screen + margin right, it's ok to move scroll using it, because we use scroll-snap, and it will auto align left side on LG sreen too.
export const ITEM_WIDTH = 76;

const HourlyScrollBar = ({ hourlyForecast }: HourlyScrollBarProps) => {
  const scrollBlockRef = useRef<HTMLDivElement>(null);
  const [scrollBlockWidth, setScrollBlockWidth] = useState(0);
  const [scrollBlockScrollLeft, setScrollBlockScrollLeft] = useState(0);
  const [buttonsActiveState, setButtonsActiveState] = useState({ left: false, right: true });

  const handleResize = useCallback(() => {
    if (scrollBlockRef.current) {
      setScrollBlockWidth(scrollBlockRef.current.scrollWidth - scrollBlockRef.current.offsetWidth);
    }
  }, [scrollBlockRef]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [scrollBlockRef, handleResize]);

  const handleBlockScroll = useCallback(() => {
    if (scrollBlockRef.current) {
      setScrollBlockScrollLeft(scrollBlockRef.current.scrollLeft);
    }
  }, [scrollBlockRef]);

  useEffect(() => {
    // This const fixes `The ref value 'scrollBlockRef.current' will likely have changed by the time this effect cleanup function runs`
    const { current } = scrollBlockRef;
    const options = {
      capture: true,
      passive: true,
    };

    if (current) {
      current.addEventListener('scroll', handleBlockScroll, options);
    }

    return () => {
      if (current) {
        current.removeEventListener('scroll', handleBlockScroll, options);
      }
    };
  }, [scrollBlockRef, handleBlockScroll]);

  useEffect(() => {
    switch (scrollBlockScrollLeft) {
      case 0:
        setButtonsActiveState({ left: false, right: true });
        break;
      case scrollBlockWidth:
        setButtonsActiveState({ left: true, right: false });
        break;
      default:
        setButtonsActiveState({ left: true, right: true });
        break;
    }
  }, [scrollBlockWidth, scrollBlockScrollLeft]);

  const moveScroll = (rotate: 'left' | 'right') => {
    if (scrollBlockRef.current) {
      scrollBlockRef.current.scrollLeft += rotate === 'right' ? ITEM_WIDTH : -ITEM_WIDTH;
    }
  };

  return (
    <div className="mt-5 flex lg:mt-0 lg:mr-0 lg:ml-[46px] xl:ml-[36px]" data-testid="hourlyScrollBarBlock">
      <ScrollButton
        rotate="left"
        move={moveScroll.bind(this)}
        isActive={buttonsActiveState.left}
        data-testid="scrollButtonLeft"
      />
      <div className="w-full lg:mx-2.5 lg:w-[212px] xl:mx-4 xl:w-[370px]" id="scrollGrid">
        <div
          ref={scrollBlockRef}
          className="no-scrollbar flex snap-x scroll-px-4 overflow-x-auto scroll-smooth px-4 lg:scroll-px-0 lg:px-0 [&>*:last-child]:mr-0"
          data-testid="hourlyScrollBarScrollableBlock"
        >
          {hourlyForecast.map((item, index) => (
            <div key={index} className="mr-2.5 h-[102px] w-[60px] shrink-0 snap-start lg:h-[110px] lg:w-16 xl:mr-3">
              <HourlyScrollItem
                time={item.time}
                icon={item.icon}
                temperature={item.temperature}
                description={item.description}
              />
            </div>
          ))}
        </div>
      </div>
      <ScrollButton
        rotate="right"
        move={moveScroll.bind(this)}
        isActive={buttonsActiveState.right}
        data-testid="scrollButtonRight"
      />
    </div>
  );
};

export default HourlyScrollBar;
